/**
 * "Try a video" — the sample-clip picker.
 *
 * A brand-new user has just finished Brand Setup and owns no source video.
 * Asking them for a YouTube URL first is asking them to wait ten minutes on a
 * download that can hit the bot wall before they have ever seen their brand
 * move. The samples catalog is a handful of clips already on the box: one
 * click and a REAL render starts, in their brand, with no download at all.
 *
 * The card is deliberately honest about availability — the server answers
 * "can this render right now?" per entry (`available` / `reason`), and an
 * unavailable card is disabled WITH the reason rather than failing ten
 * minutes later inside the pipeline.
 */
import { useEffect, useState } from "react";
import { Clapperboard, Loader2, Play } from "lucide-react";
import { listSamples, trySample, type SampleEntry } from "../api";
import { rememberJobTitle } from "../lib/trackedJobs";
import { useIsMobile } from "../lib/useIsMobile";

interface Props {
  /** Same contract as NewJob's own submit: the created job id. */
  onCreated: (jobId: string) => void;
  /** One-line lead rendered above the grid (the first-video flow sets it). */
  intro?: string;
}

/** Seconds → `m:ss`. Mirrors the Library's duration chip. */
export function formatSampleDuration(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function SamplePicker({ onCreated, intro }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [samples, setSamples] = useState<SampleEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** id of the sample whose try is in flight — one at a time, so a
   *  double-click can't race two jobs onto the same clip. */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Per-card error (409 unavailable, 404 unknown, network). Keyed by
   *  sample id so one bad card never blanks the others. */
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    listSamples()
      .then((items) => { if (!cancelled) setSamples(items); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSamples([]);
        setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, []);

  const handleTry = async (s: SampleEntry): Promise<void> => {
    if (busyId) return;
    setBusyId(s.id);
    setCardErrors((prev) => { const { [s.id]: _drop, ...rest } = prev; return rest; });
    try {
      const { id } = await trySample(s.id);
      // The job has no title of its own until the pipeline writes one, and
      // the render-ready toast fires long before that — so it named the slug
      // (QA D-6). We know the real title right here.
      rememberJobTitle(id, s.title);
      setBusyId(null);
      onCreated(id);
    } catch (err) {
      setCardErrors((prev) => ({
        ...prev,
        [s.id]: err instanceof Error ? err.message : "could not start this sample",
      }));
      setBusyId(null);
    }
  };

  if (samples === null) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ ...PANEL, padding: 40, textAlign: "center", color: "var(--ql-fg-muted)", fontSize: 13 }}
      >
        <Loader2 size={16} className="ql-spin" style={{ verticalAlign: "-3px", marginRight: 8 }} />
        Loading samples…
      </div>
    );
  }

  if (samples.length === 0) {
    return (
      <div role="status" aria-live="polite" style={{ ...PANEL, padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No samples yet.</div>
        <div style={{ fontSize: 13, color: "var(--ql-fg-muted)" }}>
          {loadError ?? "Paste a YouTube link instead, or upload a 9:16 clip."}
        </div>
        {/* The one operator-facing line in the product, and only because the
            fix genuinely is a file on the box. Muted, below the sentence a
            creator needs. */}
        {!loadError && (
          <div style={{ fontSize: 11.5, color: "var(--ql-fg-faint)", marginTop: 10 }}>
            Operator: add clips to the samples catalog (docs/operations/samples-catalog.md).
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {intro && (
        <div style={{ fontSize: 13, color: "var(--ql-gray-300)", marginBottom: 14 }}>{intro}</div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
          gap: 14,
        }}
      >
        {samples.map((s) => {
          const busy = busyId === s.id;
          const blocked = !s.available;
          const disabled = blocked || busyId !== null;
          return (
            <div
              key={s.id}
              style={{
                ...PANEL,
                padding: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                opacity: blocked ? 0.55 : 1,
              }}
            >
              <div
                style={{
                  position: "relative",
                  aspectRatio: "9 / 16",
                  maxHeight: 260,
                  background: "linear-gradient(140deg, rgba(255,213,0,.07), rgba(255,255,255,.02))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderBottom: "1px solid rgba(255,255,255,.06)",
                }}
              >
                {s.thumbUrl ? (
                  <img
                    src={s.thumbUrl}
                    alt=""
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <Clapperboard size={26} strokeWidth={1.5} color="var(--ql-fg-subtle)" />
                )}
                {s.durationSec !== undefined && (
                  <span
                    style={{
                      position: "absolute",
                      right: 8,
                      bottom: 8,
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontFamily: "var(--ql-font-mono)",
                      background: "rgba(8,9,14,.82)",
                      color: "var(--ql-fg)",
                      border: "1px solid rgba(255,255,255,.10)",
                    }}
                  >
                    {formatSampleDuration(s.durationSec)}
                  </span>
                )}
              </div>

              <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                  {s.title}
                </div>
                <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", overflowWrap: "anywhere" }}>
                  {s.speaker.name}
                  {s.speaker.description ? ` · ${s.speaker.description}` : ""}
                </div>

                {blocked && s.reason && (
                  <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 2 }}>{s.reason}</div>
                )}
                {cardErrors[s.id] && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    style={{ fontSize: 11, color: "var(--ql-danger)", marginTop: 2, overflowWrap: "anywhere" }}
                  >
                    {cardErrors[s.id]}
                  </div>
                )}

                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => void handleTry(s)}
                  title={blocked ? (s.reason ?? "this sample is not available") : "Start a real render of this clip in your brand"}
                  style={{
                    marginTop: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    border: "none",
                    cursor: disabled ? "not-allowed" : "pointer",
                    color: disabled ? "var(--ql-fg-subtle)" : "#000",
                    background: disabled
                      ? "rgba(255,255,255,.04)"
                      : "linear-gradient(135deg,#FFD500,#FFA500)",
                    boxShadow: disabled ? "none" : "0 4px 16px rgba(255,213,0,.22)",
                    transition: "all .2s var(--ql-ease)",
                  }}
                >
                  {busy ? (
                    <><Loader2 size={14} className="ql-spin" /> Starting…</>
                  ) : (
                    <><Play size={14} strokeWidth={2.2} /> Render this sample</>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PANEL: React.CSSProperties = {
  background: "rgba(16,18,27,.72)",
  border: "1px solid rgba(255,255,255,.06)",
  borderRadius: 18,
};
